/**
 * Configuración de TanStack Query.
 *
 * Los valores por defecto están pensados para un juego que va a correr dentro de
 * un WebView en un móvil, que es un entorno bastante peor que una pestaña de
 * escritorio: la conexión se cae en el ascensor, la app se queda en segundo
 * plano media hora y vuelve, y cada petición cuesta batería.
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Claves de consulta en un solo sitio.
 *
 * Escribirlas a mano en cada `useQuery` es cómo se acaba con un
 * `['user_cards', id]` en un fichero y un `['userCards', id]` en otro: dos
 * cachés distintas para el mismo dato, y una invalidación que solo refresca la
 * mitad de la pantalla. Aquí se ve además la jerarquía, que es lo que permite
 * invalidar por prefijo.
 */
export const queryKeys = {
  profile: (userId: string) => ['profile', userId] as const,
  inventory: (userId: string) => ['inventory', userId] as const,
  activeDeck: (userId: string) => ['deck', 'active', userId] as const,
  marketListings: (filters?: Record<string, unknown>) =>
    (filters ? ['market', 'listings', filters] : ['market', 'listings']) as readonly unknown[],
} as const;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // El inventario y el perfil solo cambian por acciones del propio jugador
      // (abrir un sobre, comprar), y esas ya invalidan su clave explícitamente.
      // Un minuto evita ráfagas de peticiones al navegar entre pantallas.
      staleTime: 60_000,
      gcTime: 5 * 60_000,

      // En móvil, volver a la app tras un rato dispara este evento. Refrescar
      // ahí sí interesa: el saldo puede haber cambiado desde otro dispositivo.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,

      // Dos reintentos y se rinde. Un fallo de permisos o un 404 no mejoran por
      // insistir, y en móvil cada reintento cuesta batería y segundos de espera.
      retry: (failureCount, error) => {
        const code = (error as { code?: string } | null)?.code;
        if (code === '42501' || code === 'PGRST116') return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      // Las mutaciones mueven dinero y cartas. Reintentar una compra a ciegas es
      // exactamente como se cobra dos veces: si falla, lo decide quien la llamó.
      retry: false,
    },
  },
});
